import qs from "qs";
import {useMemo} from "react";
import {useLocation, useNavigate} from "react-router";
import {z} from "zod";

export const parseUrlToSchema = <T extends z.ZodTypeAny>(schema: T, search: string) => {
  const parsed = qs.parse(search, {ignoreQueryPrefix: true});
  return schema.parse(parsed) as z.infer<T>;
};

type MapKeys<T> = {
  [K in keyof T]: T[K];
};

function useUrlState<T extends z.ZodTypeAny, TState extends MapKeys<z.infer<T>>>(
  schema: T,
): [TState, (newState: Partial<TState>) => void] {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = useMemo(() => parseUrlToSchema(schema, location.search), [location.search, schema]);

  const setQueryParams = (newState: Partial<TState>) => {
    const updatedState = {...queryParams, ...newState};
    navigate(
      {
        pathname: location.pathname,
        search: qs.stringify(updatedState),
      },
      {replace: true},
    );
  };

  const state = queryParams as TState;
  return [state, setQueryParams];
}

export default useUrlState;
